import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMessageCircle, FiX, FiSend, FiAlertTriangle, FiCheckCircle, FiTrash2, FiLock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [chatStatus, setChatStatus] = useState('none');
  const [chatHistory, setChatHistory] = useState([
    { id: 'initial', text: "Olá! Como podemos ajudar você hoje?", isBot: true },
  ]);
  const messagesEndRef = useRef(null);
  
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Carregar histórico de mensagens do backend (apenas se logado)
  useEffect(() => {
    if (user && token) {
      const fetchMessages = async () => {
        try {
          const response = await axios.get(`${API_URL}/support/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            setChatStatus(response.data.status);
            if (response.data.messages) {
              setChatHistory([
                { id: 'initial', text: "Olá! Como podemos ajudar você hoje?", isBot: true },
                ...response.data.messages
              ]);
            }
          }
        } catch (error) {
          console.error('Erro ao buscar mensagens:', error);
        }
      };
      
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    } else {
      // Limpar histórico se deslogar
      setChatHistory([{ id: 'initial', text: "Olá! Como podemos ajudar você hoje?", isBot: true }]);
      setChatStatus('none');
    }
  }, [user, token]);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [chatHistory, isOpen]);

  const toggleChat = () => setIsOpen(!isOpen);

  const handleCloseSupport = async () => {
    if (!user || !window.confirm('Deseja realmente encerrar este atendimento?')) return;

    try {
      await axios.post(`${API_URL}/support/close-customer`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const response = await axios.get(`${API_URL}/support/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatHistory([
        { id: 'initial', text: "Olá! Como podemos ajudar você hoje?", isBot: true },
        ...response.data.messages
      ]);
    } catch (error) {
      console.error('Erro ao encerrar atendimento:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!user || !window.confirm('Deseja apagar esta mensagem?')) return;

    try {
      await axios.post(`${API_URL}/support/delete-message`, 
        { messageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatHistory(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, text: 'Mensagem apagada', isDeleted: true } : msg
      ));
    } catch (error) {
      console.error('Erro ao apagar mensagem:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !user) return;

    const userMessage = message.trim();
    setMessage('');

    const localMessage = { id: Date.now(), text: userMessage, isBot: false, isStaff: false };
    setChatHistory(prev => [...prev, localMessage]);

    try {
      await axios.post(`${API_URL}/support/send`, 
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (chatHistory.length <= 1) {
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            id: 'bot-' + Date.now(),
            text: `Olá ${user.name.split(' ')[0]}! Recebemos sua mensagem. Um atendente responderá em breve.`,
            isBot: true
          }]);
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl w-[320px] sm:w-[380px] overflow-hidden border border-gray-100 mb-4 flex flex-col"
            style={{ maxHeight: '500px' }}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 flex justify-between items-center text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <FiMessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Atendimento Online</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    <span className="text-[10px] text-blue-100 uppercase tracking-wider font-medium">Online agora</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {user && chatStatus !== 'closed' && (
                  <button 
                    onClick={handleCloseSupport}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1 text-[10px] font-bold"
                    title="Encerrar Atendimento"
                  >
                    <FiCheckCircle size={14} />
                    ENCERRAR
                  </button>
                )}
                <button 
                  onClick={toggleChat} 
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Fechar chat"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-gray-50/50 min-h-[300px] flex flex-col">
              {!user ? (
                <div className="flex-grow flex flex-col items-center justify-center text-center p-6 space-y-4">
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-2">
                    <FiLock size={32} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Login Necessário</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Para sua segurança, o atendimento via chat está disponível apenas para usuários cadastrados e logados.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/login');
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-md active:scale-95"
                  >
                    ENTRAR NA MINHA CONTA
                  </button>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/login');
                    }}
                    className="text-xs text-blue-600 font-bold hover:underline"
                  >
                    Não tenho conta, quero me cadastrar
                  </button>
                </div>
              ) : (
                <>
                  {/* Security Alert */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 items-start mb-2">
                    <FiAlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16} />
                    <p className="text-[10px] text-amber-800 leading-tight font-medium">
                      <span className="font-bold block mb-0.5 uppercase tracking-wider">Aviso de Segurança:</span>
                      Nunca solicitamos pagamentos ou transferências via PIX diretamente por este chat. Use apenas os canais oficiais do site.
                    </p>
                  </div>

                  {chatHistory.map((chat) => (
                    <div key={chat.id} className={`flex ${chat.isBot || chat.isStaff ? 'justify-start' : 'justify-end'} animate-fade-in`}>
                      <div className={`max-w-[85%] p-3 rounded-2xl shadow-sm relative group ${
                        chat.isBot || chat.isStaff 
                          ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100' 
                          : 'bg-blue-600 text-white rounded-tr-none'
                      }`}>
                        {chat.isStaff && (
                          <span className="text-[10px] font-bold text-blue-600 uppercase mb-1 block">Atendente</span>
                        )}
                        <p className={`text-sm leading-relaxed ${chat.isDeleted ? 'italic text-gray-400 opacity-60' : ''}`}>
                          {chat.text}
                        </p>

                        {/* Botão de Apagar (apenas para mensagens do user e não apagadas) */}
                        {!chat.isBot && !chat.isStaff && !chat.isDeleted && chat.id !== 'initial' && (
                          <button
                            onClick={() => handleDeleteMessage(chat.id)}
                            className="absolute -left-8 top-1/2 -translate-y-1/2 p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                            title="Apagar mensagem"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-gray-100 flex gap-2 shrink-0">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={user ? "Como podemos ajudar?" : "Faça login para conversar"}
                disabled={!user}
                className="flex-grow bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none disabled:cursor-not-allowed"
              />
              <button 
                type="submit"
                disabled={!message.trim() || !user}
                className={`p-2 rounded-xl transition-all shadow-md ${
                  message.trim() && user
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/20 active:scale-95' 
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed shadow-none'
                }`}
              >
                <FiSend size={20} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleChat}
        aria-label={isOpen ? "Fechar chat" : "Abrir chat"}
        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 relative ${
          isOpen ? 'bg-white text-gray-500 hover:bg-gray-50 rotate-90' : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isOpen ? (
          <FiX size={28} />
        ) : (
          <div className="relative">
            <FiMessageCircle size={28} />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 border-2 border-blue-600 rounded-full animate-pulse"></span>
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default ChatWidget;
